import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode
} from "react";

import {
  isAuthEnabled,
  signInWithGoogle,
  signOutUser,
  subscribeToAuthState,
  type AuthUser
} from "../browser/firebaseAuth";

interface AuthGateProps {
  children: ReactNode;
}

export const AUTH_GATE_LOADING_TITLE = "Welcome back";
export const AUTH_GATE_LOADING_MESSAGE = "We’re getting your workspace ready.";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const prefersReducedMotion = (): boolean =>
  typeof globalThis.window !== "undefined" &&
  typeof globalThis.window.matchMedia === "function" &&
  globalThis.window.matchMedia(REDUCED_MOTION_QUERY).matches;

function usePrefersReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);

  useEffect(() => {
    if (typeof globalThis.window === "undefined") {
      return undefined;
    }

    const mediaQuery = globalThis.window.matchMedia?.(REDUCED_MOTION_QUERY);

    if (!mediaQuery) {
      return undefined;
    }

    const handleChange = (event: MediaQueryListEvent) => {
      setReducedMotion(event.matches);
    };

    mediaQuery.addEventListener?.("change", handleChange);

    return () => {
      mediaQuery.removeEventListener?.("change", handleChange);
    };
  }, []);

  return reducedMotion;
}

type AuthState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "signed-in"; user: AuthUser };

interface AuthSession {
  enabled: boolean;
  status: AuthState["status"];
  user: AuthUser | null;
  signOut: () => Promise<void>;
}

const defaultAuthSession: AuthSession = {
  enabled: false,
  status: "signed-out",
  user: null,
  signOut: signOutUser
};

const AuthSessionContext = createContext<AuthSession>(defaultAuthSession);

export const useAuthSession = (): AuthSession => useContext(AuthSessionContext);

/**
 * Gates the whole app behind Google sign-in when auth is enabled (production
 * build). In local dev (auth disabled) it renders children immediately and
 * never touches the Firebase SDK. When enabled and signed in, it exposes the
 * identity to the app shell so account controls live in the left navigation.
 */
export function AuthGate({ children }: AuthGateProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [state, setState] = useState<AuthState>(
    isAuthEnabled() ? { status: "loading" } : { status: "signed-out" }
  );
  const [signInError, setSignInError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToAuthState((user) => {
      setState(user ? { status: "signed-in", user } : { status: "signed-out" });
    });
  }, []);

  if (!isAuthEnabled()) {
    return (
      <AuthSessionContext.Provider value={defaultAuthSession}>
        {children}
      </AuthSessionContext.Provider>
    );
  }

  if (state.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] px-4 text-[var(--ink)]">
        <main
          aria-busy="true"
          aria-label="Titan Controls Specifier sign-in"
          data-auth-gate="loading"
          data-auth-gate-reduced-motion={reducedMotion ? "true" : "false"}
          className="w-full max-w-[480px] rounded-[16px] border border-[var(--line)] bg-[var(--surface)] px-8 py-10 text-center shadow-sm"
        >
          <div
            aria-hidden="true"
            className="mx-auto flex h-[32px] w-[32px] flex-none items-center justify-center rounded-lg bg-[var(--ink)]"
          >
            <div className="h-[14px] w-[14px] rounded-[3px] border-[2.5px] border-[var(--bg)]" />
          </div>
          <div className="mt-5 text-[11px] font-bold uppercase tracking-[.11em] text-[var(--faint)]">
            Titan Specifier
          </div>
          <h1 className="mt-2 text-[28px] font-extrabold leading-none tracking-[-.035em]">
            {AUTH_GATE_LOADING_TITLE}
          </h1>
          <p
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="mt-3 text-sm leading-[1.55] text-[var(--muted)]"
          >
            {AUTH_GATE_LOADING_MESSAGE}
          </p>
          <div
            aria-hidden="true"
            className="mx-auto mt-6 h-1 w-28 overflow-hidden rounded-full bg-[var(--raised2)]"
          >
            <span
              className={
                reducedMotion
                  ? "block h-full w-1/3 rounded-full bg-[var(--ink)]"
                  : "animate-tcsLoading block h-full w-1/3 rounded-full bg-[var(--ink)]"
              }
            />
          </div>
        </main>
      </div>
    );
  }

  if (state.status === "signed-out") {
    const handleSignIn = () => {
      setSignInError(null);
      signInWithGoogle().catch((error: unknown) => {
        setSignInError(
          error instanceof Error ? error.message : "Sign-in failed. Please try again."
        );
      });
    };

    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] px-4 text-[var(--ink)]">
        <div className="w-full max-w-sm rounded-2xl border border-[var(--line)] bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-extrabold text-[var(--ink)]">
            Titan Controls Specifier
          </h1>
          <p className="mt-2 text-sm text-[var(--faint)]">
            Sign in with your Automatry Google account to continue.
          </p>
          <button
            type="button"
            onClick={handleSignIn}
            className="mt-6 w-full rounded-full border border-[var(--line)] bg-[var(--ink)] px-4 py-2 text-sm font-bold text-white transition hover:opacity-90"
          >
            Sign in with Google
          </button>
          <p className="mt-3 text-xs text-[var(--faint)]">
            Access is restricted to @automatry.com accounts.
          </p>
          {signInError ? (
            <p role="alert" className="mt-3 text-xs font-semibold text-red-600">
              {signInError}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <AuthSessionContext.Provider
      value={{
        enabled: true,
        status: "signed-in",
        user: state.user,
        signOut: signOutUser
      }}
    >
      {children}
    </AuthSessionContext.Provider>
  );
}
